import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { OccurrenceRow } from "@/components/teacher/schedule/calendar/OccurrenceRow";
import type { WeeklyItem, StudentInfo, ClassTemplateEvent } from "@/types/schedule";

function dateISO(d: Date) { return d.toISOString().slice(0,10); }



interface TemplateRow {
  id: string;
  subject: string;
  location: string;
  weekday: number;
  start_time: string;
  end_time: string;
  students: any;
  created_at: string;
  start_date: string | null;
}

export const UpcomingNext7: React.FC<{ teacherCode?: string }> = ({ teacherCode }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<WeeklyItem[]>([]);
  const [events, setEvents] = useState<ClassTemplateEvent[]>([]);
  const [newTemplateIds, setNewTemplateIds] = useState<Set<string>>(new Set());

  const today = useMemo(()=> new Date(), []);
  const rangeStartISO = dateISO(today);

  const fetchData = async () => {
    if (!teacherCode) { setItems([]); return; }
    setLoading(true);

    const [jobs, w1, eventsRes] = await Promise.all([
      (supabase as any).from('v_class_templates').select('id, subject, location, weekday, start_time, end_time, students, created_at, start_date').eq('teacher_code', teacherCode).eq('is_active', true),
      (supabase as any).rpc('get_teacher_weekly_schedule', { _teacher_code: teacherCode, _week_start: rangeStartISO }),
      (supabase as any).from('v_class_template_events').select('*').eq('requested_by', teacherCode).gte('event_date', rangeStartISO),
    ]);

    setEvents((eventsRes.data ?? []) as ClassTemplateEvent[]);

    const jobRows = (jobs.data ?? []) as TemplateRow[];

    // Identify new templates (created within last 7 days)
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    const newIds = new Set<string>();
    for (const j of jobRows) {
      if (j.created_at && new Date(j.created_at) >= sevenDaysAgo) {
        newIds.add(j.id);
      }
    }
    setNewTemplateIds(newIds);

    const studentsByJob = new Map<string, StudentInfo[]>();
    for (const j of jobRows) studentsByJob.set(j.id, (j.students ?? []) as StudentInfo[]);

    const projections: WeeklyItem[] = [];
    for (let i = 0; i < 7; i++) {
      const dt = new Date(today);
      dt.setDate(today.getDate() + i);
      const dow = dt.getDay();
      const dateStr = dateISO(dt);
      for (const j of jobRows) {
        if (j.weekday === dow) {
          projections.push({
            fixed_job_id: j.id, occurrence_id: '', date: dateStr,
            subject: j.subject, location: j.location, start_time: j.start_time, end_time: j.end_time,
            duration_minutes: 0, status: 'SCHEDULED', confirmed: false, edit_status: 'NONE',
            insufficient_hours: false, students: j.students, occurrence_edit_requests: [],
          });
        }
      }
    }

    const occs: WeeklyItem[] = ((w1.data?.schedule ?? []) as WeeklyItem[]).map((o) => ({
      ...o, students: (studentsByJob.get(o.fixed_job_id) ?? o.students) as any,
    }));

    const occMap = new Map<string, WeeklyItem>();
    for (const o of occs) occMap.set(`${o.date}|${o.fixed_job_id}`, o);
    const projKeys = new Set<string>();
    const combined: WeeklyItem[] = [];
    for (const p of projections) {
      const key = `${p.date}|${p.fixed_job_id}`;
      projKeys.add(key);
      combined.push(occMap.get(key) ?? p);
    }
    for (const o of occs) {
      const key = `${o.date}|${o.fixed_job_id}`;
      if (!projKeys.has(key)) combined.push(o);
    }

    setItems(combined.sort((a,b)=> (a.date+a.start_time).localeCompare(b.date+b.start_time)));
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [teacherCode, rangeStartISO]);

  if (!teacherCode) return <div className="text-muted-foreground">กรุณากรอกรหัสครูในข้อมูลส่วนตัว</div>;
  if (loading) return <div className="text-muted-foreground">กำลังโหลด...</div>;
  if (items.length === 0) return <div className="text-muted-foreground">ไม่มีคาบใน 7 วันถัดไป</div>;

  const grouped = items.reduce<Record<string, WeeklyItem[]>>((acc, it)=>{
    (acc[it.date] ||= []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-4">

      {Object.entries(grouped).map(([date, arr]) => (
        <div key={date} className="rounded border">
          <div className="px-4 py-2 font-semibold bg-muted">{new Date(date).toLocaleDateString('th-TH')}</div>
          <div className="p-4 space-y-2">
            {arr.sort((a,b)=>a.start_time.localeCompare(b.start_time)).map(item => (
              <OccurrenceRow
                key={`${item.fixed_job_id}-${item.date}`}
                item={item}
                teacherCode={teacherCode}
                events={events}
                onRefresh={fetchData}
                isNew={newTemplateIds.has(item.fixed_job_id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
